import { BrandSubscriptionModel, getSubscriptionFromCache } from 'owa-brands-subscription';
import loc from 'owa-localize';
import { unsubscribe } from 'owa-locstrings/lib/strings/unsubscribe.locstring.json';
import getSelectedTableViewId from 'owa-mail-list-store/lib/utils/getSelectedTableViewId';
import { ItemViewState } from 'owa-mail-reading-pane-store';
import { lazyUnsubscribeEmail } from 'owa-mail-triage-action';
import * as lazyTriageActions from 'owa-mail-triage-action';
import { lazyMountAndShowFullOptions } from 'owa-options-view';
import Item from 'owa-service/lib/contract/Item';
import Message from 'owa-service/lib/contract/Message';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import { isFeatureEnabled } from 'owa-feature-flags';
import { logUsage } from 'owa-analytics';
import {
    manageSubscriptionInfobarText,
    unsubscribeInfobarText,
} from './createUnsubscribeInfoBarMessage.locstring.json';
import {
    InfoBarCustomAction,
    InfoBarMessageColor,
    InfoBarMessageRank,
    InfoBarMessageSource,
    InfoBarMessageViewState,
} from 'owa-info-bar/lib/schema/InfoBarMessageViewState';

/**
 * Gets a boolean of whether the unsubscribe info bar should be created
 * @param item the item
 * @return true if the info bar should be created, false otherwise
 */
function shouldCreateUnsubscribeInfoBar(item: Item): boolean {
    // Create unsubscribe info bar for NON-ShadowMailbox users if reportJunkEnabled is enabled,
    // and the message is a newsletter, i.e. has 'Newsletters' hashtag or system category
    return (
        !getUserConfiguration().SessionSettings.IsShadowMailbox &&
        getUserConfiguration().SegmentationSettings.ReportJunkEmailEnabled &&
        ((item.Hashtags && item.Hashtags.indexOf('Newsletters') != -1) ||
            (item.SystemCategories && item.SystemCategories.indexOf(15) != -1))
    );
}

/**
 * Create the unsubscribe info bar if necessary
 * @param message the message
 * @param key the key of info bar message
 * @return the unsubscribe InfoBarMessageViewState
 */
export default function tryCreateUnsubscribeInfoBarMessage(
    message: Message,
    key: string,
    itemViewState: ItemViewState
): InfoBarMessageViewState {
    let cacheEntry: BrandSubscriptionModel = null;

    if (isFeatureEnabled('rp-infoBarBrandSubscription')) {
        // if the sender doesn't set the Sender Smtp header correctly, the Message.Sender doens't have smtp address set.
        // In this case we fall back to the From property.
        const emailAddress = getSenderEmailAddress(message);
        if (!emailAddress) {
            return null;
        }

        cacheEntry = getSubscriptionFromCache(emailAddress);
        if (!cacheEntry) {
            return null;
        }
    } else {
        if (!shouldCreateUnsubscribeInfoBar(message)) {
            return null;
        }
    }

    const unsubscribeAction: InfoBarCustomAction = {
        action: async () => {
            if (isFeatureEnabled('rp-infoBarBrandSubscription')) {
                const unsubscribeEmailV2 = await lazyTriageActions.lazyUnsubscribeEmailV2.import();
                unsubscribeEmailV2(message, cacheEntry, 'InfoBar', itemViewState);
            } else {
                lazyUnsubscribeEmail
                    .import()
                    .then(unsubsribeAction =>
                        unsubsribeAction(message.ItemId.Id, getSelectedTableViewId(), 'InfoBar')
                    );
            }
        },

        text: loc(unsubscribe),
    };

    let manageSubscriptionsAction: InfoBarCustomAction = null;
    if (isFeatureEnabled('doc-brandsSubscriptions')) {
        manageSubscriptionsAction = {
            action: () => {
                logUsage('BrandSubscriptionInfoBarClicked');
                lazyMountAndShowFullOptions.importAndExecute('mail', 'brandsSubscriptions');
            },
            text: loc(manageSubscriptionInfobarText),
        };
    }

    return <InfoBarMessageViewState>{
        key: key,
        source: InfoBarMessageSource.ReadingPane,
        rank: InfoBarMessageRank.Safety,
        color: InfoBarMessageColor.Grey,
        messageParts: [loc(unsubscribeInfobarText), unsubscribeAction].concat(
            manageSubscriptionsAction ? ['|', manageSubscriptionsAction] : []
        ),
    };
}

function getSenderEmailAddress(message: Message): string | null {
    return message.Sender?.Mailbox?.EmailAddress
        ? message.Sender.Mailbox.EmailAddress
        : message.From?.Mailbox?.EmailAddress
        ? message.From.Mailbox.EmailAddress
        : null;
}
